import { useCallback, useEffect, useMemo, useRef } from 'react';

import * as Comlink from 'comlink';
import { useShallow } from 'zustand/react/shallow';

import { createCanvasElement, stopTracks } from '@src/helpers';
import { useVirtualBackgroundStore, useVideoStreamStore } from '@src/stores';
import { virtualBackgroundService, VideoStreamService } from '@src/services';

interface IUseVirtualBackgroundPreviewProps {
  enabled?: boolean;
}

export const useVirtualBackgroundPreview = ({ enabled = true }: IUseVirtualBackgroundPreviewProps = {}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewStreamRef = useRef<MediaStream | null>(null);
  const isInitializedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Subscribe to selectedEffect and appliedEffect
  const { selectedEffect, appliedEffect } = useVirtualBackgroundStore(
    useShallow((state) => ({
      selectedEffect: state.selectedEffect,
      appliedEffect: state.appliedEffect,
    }))
  );

  // Subscribe to originalStream AND modifiedStream for fallback logic
  const { originalStream, modifiedStream } = useVideoStreamStore(
    useShallow((state) => ({
      originalStream: state.originalStream,
      modifiedStream: state.modifiedStream,
    }))
  );

  // Memoize video dimensions to prevent recalculation on every render
  const { width, height } = useMemo(() => {
    const videoTrack = originalStream?.getVideoTracks()[0];
    const settings = videoTrack?.getSettings();
    return {
      width: settings?.width || 1280,
      height: settings?.height || 720,
    };
  }, [originalStream]);

  // Memoize effect comparison to prevent unnecessary recalculations
  const effectsMatch = useMemo(
    () => selectedEffect.type === appliedEffect.type && selectedEffect.value === appliedEffect.value,
    [selectedEffect.type, selectedEffect.value, appliedEffect.type, appliedEffect.value]
  );

  // Memoize preview flags - these only change when actual conditions change
  const { shouldShowOriginalAsPreview, shouldEnableEffectPreview } = useMemo(
    () => ({
      shouldShowOriginalAsPreview: enabled && selectedEffect.type === 'none',
      shouldEnableEffectPreview: enabled && selectedEffect.type !== 'none' && !effectsMatch,
    }),
    [enabled, selectedEffect.type, effectsMatch]
  );

  // 1. Create Canvas and Stream for effect preview
  useEffect(() => {
    if (!shouldEnableEffectPreview) {
      // Cleanup preview stream if switching to 'none'
      if (previewStreamRef.current) {
        stopTracks(previewStreamRef.current);
        previewStreamRef.current = null;
      }
      return;
    }

    if (!canvasRef.current) {
      // Create a hidden canvas for preview
      const canvas = createCanvasElement({ width, height });
      canvasRef.current = canvas;
    }

    return () => {
      // Proper cleanup: stop all tracks of the captured stream
      if (previewStreamRef.current) {
        stopTracks(previewStreamRef.current);
        previewStreamRef.current = null;
      }

      if (canvasRef.current) {
        if (canvasRef.current.parentNode) {
          canvasRef.current.parentNode.removeChild(canvasRef.current);
        }
        canvasRef.current = null;
      }
    };
  }, [shouldEnableEffectPreview, width, height]);

  // 2. Initialize Worker with Canvas
  useEffect(() => {
    // Capture the current canvas instance synchronously to avoid ref mutation races
    const currentCanvas = canvasRef.current;

    if (!shouldEnableEffectPreview || !currentCanvas || isInitializedRef.current) return;

    let isMounted = true;

    const initPreview = async () => {
      try {
        // Use getWorkerApi to ensure worker is initialized (increments refCount)
        const api = await virtualBackgroundService.getWorkerApi();

        // RACE CHECK: Ensure we are still mounted and targeting the same canvas
        if (!isMounted || !canvasRef.current || canvasRef.current !== currentCanvas) {
          // If raced, we must release the worker we just acquired!
          virtualBackgroundService.releaseWorker();
          return;
        }

        if (!api) return;

        // Transfer control - worker will draw to this canvas
        // Wrap in try-catch specifically for transfer error to avoid noise if raced
        try {
          const offscreen = currentCanvas.transferControlToOffscreen();
          await api.setPreviewCanvas(Comlink.transfer(offscreen, [offscreen]), width, height);
          if (!previewStreamRef.current) {
            previewStreamRef.current = currentCanvas.captureStream(30);
          }

          isInitializedRef.current = true;

          // Directly update video element with preview stream
          VideoStreamService.assignStream(videoRef.current, previewStreamRef.current);

          // Sync initial effect
          await api.setPreviewEffect(useVirtualBackgroundStore.getState().selectedEffect);
        } catch (e) {
          console.warn('[VB Preview] Transfer failed (likely raced):', e);
        }
      } catch (error) {
        console.error('[VB Preview] Failed to init preview:', error);
      }
    };

    initPreview();

    return () => {
      isMounted = false;
      // Cleanup on unmount or disable
      if (isInitializedRef.current) {
        Promise.resolve(virtualBackgroundService.getActiveWorker()).then((api) => {
          api?.removePreviewCanvas().catch(console.warn);
        });
        isInitializedRef.current = false;
        // Release worker ref
        virtualBackgroundService.releaseWorker();
      }
    };
  }, [shouldEnableEffectPreview, width, height]);

  // 3. Sync Effect Changes
  useEffect(() => {
    if (!isInitializedRef.current || !shouldEnableEffectPreview) return;

    Promise.resolve(virtualBackgroundService.getActiveWorker()).then((api) => {
      api?.setPreviewEffect(selectedEffect).catch((err) => console.error('[VB Preview] Failed to set effect:', err));
    });
  }, [selectedEffect, shouldEnableEffectPreview]);

  // Compute the final stream to use (using refs directly)
  // Priority: previewStream (if different effect) > originalStream (if 'none') > modifiedStream > originalStream
  const getFinalStream = useCallback((): MediaStream | null => {
    if (!enabled) {
      return modifiedStream || originalStream;
    }
    if (effectsMatch) {
      return modifiedStream || originalStream;
    }
    if (shouldShowOriginalAsPreview) {
      return originalStream;
    }
    return previewStreamRef.current || modifiedStream || originalStream;
  }, [enabled, effectsMatch, shouldShowOriginalAsPreview, modifiedStream, originalStream]);

  // Callback ref for video element - handles stream assignment using VideoStreamService
  // IMPORTANT: When callback ref dependencies change, React calls OLD callback with null,
  // then NEW callback with element. We must NOT clear the stream during this transition.
  const setVideoRef = useCallback(
    (videoEl: HTMLVideoElement | null) => {
      // Skip if called with the same element (prevents re-assignment on callback ref change)
      if (videoRef.current === videoEl) {
        return;
      }

      // When videoEl is null, it could be:
      // 1. Callback ref change (React cleanup) - DON'T clear stream, just update ref
      // 2. Actual element unmount - stream cleanup is handled by the unmount useEffect
      // So we only update the ref, not the stream
      if (videoEl === null) {
        videoRef.current = null;
        return;
      }

      // New element - cleanup old element if it exists
      if (videoRef.current && videoRef.current !== videoEl) {
        VideoStreamService.assignStream(videoRef.current, null);
      }

      videoRef.current = videoEl;

      // Assign stream to the new element
      const stream = getFinalStream();
      VideoStreamService.assignStream(videoEl, stream);
    },
    [getFinalStream]
  );

  // Update video element when stream changes
  // Skip if video element already has the correct stream to avoid flicker
  useEffect(() => {
    if (!videoRef.current) return;

    const stream = getFinalStream();
    // Only update if the stream is actually different
    if (videoRef.current.srcObject !== stream) {
      VideoStreamService.assignStream(videoRef.current, stream);
    }
  }, [getFinalStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      VideoStreamService.assignStream(videoRef.current, null);
    };
  }, []);

  const cleanup = useCallback(() => {
    if (previewStreamRef.current) {
      stopTracks(previewStreamRef.current);
      previewStreamRef.current = null;
    }

    if (canvasRef.current) {
      // Try to remove from DOM if it was attached (standard check)
      if (canvasRef.current.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current);
      }
      canvasRef.current = null;
    }

    if (isInitializedRef.current) {
      isInitializedRef.current = false;
      virtualBackgroundService.releaseWorker();
    }
  }, []);

  // Return callback ref, stream, and cleanup
  return {
    setVideoRef,
    activeStream: getFinalStream(),
    cleanup,
  };
};
